import React, {
  useCallback, useContext, useEffect,
  useState,
} from 'react';
import { withI18n } from 'react-i18next';
import shortid from 'shortid';
import { Typography, withStyles } from '@material-ui/core';
import ProductPageItem from '@/components/Products/ProductPageItem';
import Loader from '@/components/Layout/Loader';
import FaqProducts from '@/components/FrequentlyQuestions/FaqProducts';
import FilterProducts from '@/components/Filters/FilterProducts';
import NoResultMessageTrans from '@/components/Notification/NoResultMessageTrans';
import FilterCard from '@/components/Cards/FilterCard';
import FilterButton from '@/components/Buttons/FilterButton';
import ActionButton from '@/components/Buttons/ActionButton';
import { SITE_HG } from '@/config';
import { HOSTING_URL } from '@/config/urls/hostingUrls';
import { ProductsContext } from '@/contexts/Products/context';
import { BottomAdvertisementHandler } from '@/pages/advertisement/BottomAdvertisementHandler';
import styles from './styles';
import ResellerMessage from './ResellerMessage';
import PageMessages from '@/pages/common/PageMessages';
import { useResize } from '@/hooks/useResize';
import { useDispatch, useSelector } from 'react-redux';
import { domainsActions } from '@/redux/modules';
import { TempDomainModal } from '../../TempDomainModal';
import { ModalAccessN8n } from '@/components/Products/ModalAccessN8n';
import useLocale from '@/hooks/useLocale';
import { useAiChat } from '@/hooks/AiChat';

const LOCATION_PRODUCTS = 'produtos';

const ProductPageContent = ({ classes, t }) => {
  const {
    featureOldResellerNotification,
    loading,
    loadMore,
    oldResellerId,
    products,
    productsOldResellers,
    shouldDisplayResellerMessage,
    suspendedProducts,
    totalProducts,
    visible,
    productsByStatus,
  } = useContext(ProductsContext);

  const { aiChatForm } = useAiChat();

  const dispatch = useDispatch();
  const { products: productsLocale } = useLocale();
  const { temporaryDomains } = useSelector(state => state.newDomains);
  const { n8n: n8nState } = useSelector(state => state.products);
  const [temporaryDomainModal, setTemporaryDomainModal] = useState([]);
  const [temporaryDomainModalOption, setTemporaryDomainModalOption] = useState(null);
  const [showModalN8n, setShowModalN8n] = useState(false);
  const modalN8nLocale = productsLocale.modalN8n;

  const windowRect = useResize();
  const { width } = windowRect;
  const desktop = width > 1280;

  const getTemporaryDomains = useCallback(() => {
    if (!temporaryDomains.temporaryDomainsLoaded) {
      dispatch(domainsActions.temporaryDomains.request());
    }
  }, [dispatch, temporaryDomains.temporaryDomainsLoaded]);

  useEffect(() => {
    getTemporaryDomains();
  }, [
    getTemporaryDomains,
  ]);

  const handleShowTempDomainModal = useCallback(({ temporaryDomains, label }) => {
    setTemporaryDomainModal(temporaryDomains);
    setTemporaryDomainModalOption(label);
  }, []);

  const modalN8nProps = useCallback(() => {
    const handleClose = () => {
      setShowModalN8n(false);
    };

    return {
      modalN8nLocale,
      n8nState,
      handleClose,
    };
  }, [modalN8nLocale, n8nState, setShowModalN8n]);

  const filterButton = (
    <FilterButton target={SITE_HG}>
      {t('hireProduct')}
    </FilterButton>
  );

  if (loading) {
    return <Loader />;
  }

  if (totalProducts.length === 0) {
    return (
      <NoResultMessageTrans
        languageKey="noResults.tab.products"
        urlTarget={HOSTING_URL}
      />
    );
  }

  const showLoadMore = visible < products.length;

  const hostingProducts = products
    .slice(0, visible)
    .filter(product => product && product.origin === 'hosting');


  const clearTemporayDomainModal = () => {
    setTemporaryDomainModal(null);
    setTemporaryDomainModalOption(null);
  };

  return (
    <>
      {showModalN8n && (
        <ModalAccessN8n {...modalN8nProps()} />
      )}

      {temporaryDomainModal && !!temporaryDomainModal.length && (
        <TempDomainModal
          temporaryDomainModal={temporaryDomainModal}
          setTemporaryDomainModal={setTemporaryDomainModal}
          onClose={clearTemporayDomainModal}
          optionLabel={temporaryDomainModalOption}
        />
      )}

      {aiChatForm({ withoutMargin: false })}

      { (shouldDisplayResellerMessage && featureOldResellerNotification) && (
        <ResellerMessage />
      )}

      <PageMessages
        displayDomainAlertManager={false}
        displayDomainPropagationAlert
      />

      <div className={classes.contentWrapper}>
        <FilterCard title={t('lists.products')} button={filterButton} useMediaWidth={1280}>
          {(totalProducts.length > 4 || desktop) && <FilterProducts total={totalProducts.length} />}
        </FilterCard>

        {products.length === 0 ? (
          <Typography variant="caption" className={classes.message}>{t('noResults.filter')}</Typography>
        ) : (
          hostingProducts.length > 0 && hostingProducts.map(product => (
            <ProductPageItem
              key={shortid.generate()}
              id={product.id}
              product={product}
              oldResellerId={oldResellerId}
              productsOldResellers={productsOldResellers}
              suspendedProducts={suspendedProducts}
              products={products}
              temporaryDomains={temporaryDomains}
              handleShowTempDomainModal={handleShowTempDomainModal}
              showModalN8n={showModalN8n}
              setShowModalN8n={setShowModalN8n}
              productsByStatus={productsByStatus}
            />
          ))
        )}

        {showLoadMore && (
          <ActionButton action={loadMore}>
            {t('btn.loadMore')}
          </ActionButton>
        )}
      </div>
      <div className={classes.bottomInfoWrapper} data-testid="bottom-faq-products-content">
        <FaqProducts />
        <BottomAdvertisementHandler advertisementLocal={LOCATION_PRODUCTS} />
      </div>
    </>
  );
};

export default withI18n()(withStyles(styles)(ProductPageContent));
